/**
 * Phone numbers, by each country's real numbering rules (libphonenumber).
 *
 * Mirrors validate_phone in app/schemas/auth.py: a number is valid when
 * libphonenumber says so for its country, it is stored in E.164
 * ("+919876543210"), and a number typed without "+" is read as Indian.
 *
 * The `max` metadata is used on purpose: `min` only checks a number's length,
 * so it accepts "+91 01234 56789" (no Indian number starts with 0) and cannot
 * tell a mobile from a landline -- the backend would then refuse what the form
 * let through. With the mobile examples (the placeholder) it is about 266 kB
 * minified / 67 kB gzipped, against 189 kB / 44 kB for `min`.
 */
import {
  Metadata,
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  parsePhoneNumberFromString,
} from 'libphonenumber-js/max';

/** A number without "+" is read as Indian, as the backend does. */
export const DEFAULT_PHONE_COUNTRY = 'IN';

/** The characters a phone number may be typed with. Mirrors the backend. */
export const PHONE_CHARACTERS = /^\+?[\d\s\-().]+$/;

export const PHONE_MESSAGES = {
  characters: 'Phone may contain digits, spaces, dashes and a leading +',
  countryCode: 'Include the country code',
  filler: 'Enter a real phone number',
  invalidFor: (country) => `Enter a valid phone number for ${countryName(country)}`,
};

let displayNames;
/** "India" for "IN": the English name a person recognises. */
export function countryName(country) {
  try {
    displayNames ??= new Intl.DisplayNames(['en'], { type: 'region' });
    return displayNames.of(country) ?? country;
  } catch {
    return country;
  }
}

/** 🇮🇳 for "IN": two regional-indicator letters. */
export function flagEmoji(country) {
  return String.fromCodePoint(...[...country.toUpperCase()].map((c) => 0x1f1a5 + c.charCodeAt(0)));
}

let countries;
/** Every country, India first, then A to Z by name. */
export function phoneCountries() {
  if (!countries) {
    const all = getCountries().map((code) => ({
      code,
      name: countryName(code),
      dialCode: getCountryCallingCode(code),
      flag: flagEmoji(code),
    }));
    all.sort((a, b) => a.name.localeCompare(b.name, 'en'));
    const india = all.findIndex((country) => country.code === DEFAULT_PHONE_COUNTRY);
    countries = [all[india], ...all.slice(0, india), ...all.slice(india + 1)];
  }
  return countries;
}

let metadata;
/** The main country for a calling code: "US" for 1, "GB" for 44 -- as the backend picks. */
function mainCountryFor(callingCode) {
  metadata ??= new Metadata();
  return metadata.getCountryCodesForCallingCode(String(callingCode))?.[0];
}

/** The calling code a "+..." string starts with, when it names a real one. */
function dialledCountry(value) {
  const digits = value.replace(/\D/g, '');
  for (const length of [1, 2, 3]) {
    const country = mainCountryFor(digits.slice(0, length));
    if (country) return country;
  }
  return undefined;
}

/** Every digit the same, or nothing but zeros apart from one digit. */
function isFiller(nationalNumber) {
  return new Set(nationalNumber).size === 1 || nationalNumber.replace(/0/g, '').length <= 1;
}

function parse(value) {
  const international = value.startsWith('+');
  return parsePhoneNumberFromString(value, international ? undefined : DEFAULT_PHONE_COUNTRY);
}

/**
 * What is wrong with a phone number, in the backend's words, or null when it
 * is fine. Blank is fine: it means "no phone".
 */
export function phoneProblem(raw) {
  const value = (raw ?? '').trim();
  if (!value) return null;
  if (!PHONE_CHARACTERS.test(value)) return PHONE_MESSAGES.characters;
  const international = value.startsWith('+');
  const number = parse(value);
  if (!number) {
    if (!international) return PHONE_MESSAGES.invalidFor(DEFAULT_PHONE_COUNTRY);
    const country = dialledCountry(value);
    return country ? PHONE_MESSAGES.invalidFor(country) : PHONE_MESSAGES.countryCode;
  }
  const country = mainCountryFor(number.countryCallingCode);
  if (!country) return PHONE_MESSAGES.countryCode;
  if (!number.isValid()) {
    if (!international) {
      // Not Indian, but a real number in some country once "+" goes in front.
      const withPlus = parsePhoneNumberFromString(`+${value}`);
      if (withPlus?.isValid() && withPlus.country) return PHONE_MESSAGES.countryCode;
    }
    return PHONE_MESSAGES.invalidFor(country);
  }
  if (isFiller(number.nationalNumber)) return PHONE_MESSAGES.filler;
  return null;
}

/** "+919876543210" for any valid spelling of it; the input unchanged otherwise. */
export function toE164(raw) {
  const value = (raw ?? '').trim();
  if (!value) return '';
  const number = parse(value);
  return number?.isValid() ? number.number : value;
}

/** "+91 98765 43210" for display; anything unparseable comes back as it was. */
export function formatPhone(raw) {
  const value = (raw ?? '').trim();
  if (!value) return '';
  const number = parse(value);
  return number?.isValid() ? number.formatInternational() : value;
}

/**
 * Split a stored number into the country picker and the number box. A saved
 * number that does not parse keeps its text, so nothing the student typed
 * disappears; it is re-checked only when they edit or save.
 */
export function splitPhone(raw) {
  const value = (raw ?? '').trim();
  if (!value) return { country: DEFAULT_PHONE_COUNTRY, national: '' };
  const number = PHONE_CHARACTERS.test(value) ? parse(value) : undefined;
  const country = number && (number.country ?? mainCountryFor(number.countryCallingCode));
  if (!country) return { country: DEFAULT_PHONE_COUNTRY, national: value };
  if (number.isValid()) return { country, national: withoutDialCode(number) };
  // Not a valid number: show the digits as they were, never a "corrected" guess.
  return { country, national: value.startsWith('+') ? number.nationalNumber : value };
}

/** "98765 43210" from +91 98765 43210: the international format, minus "+91 ". */
function withoutDialCode(number) {
  return number.formatInternational().replace(`+${number.countryCallingCode}`, '').trim();
}

/** The national example for a country's mobiles, as the placeholder shows it. */
export function nationalExample(country, examples) {
  const number = getExampleNumber(country, examples);
  return number ? withoutDialCode(number) : '';
}

/** Whether a country is the main one for its dial code (US for +1, GB for +44). */
export function isMainCountry(country) {
  return mainCountryFor(getCountryCallingCode(country)) === country;
}

/**
 * The one string the form holds for a country plus what was typed in the
 * number box: "+<dial code><digits>" when it parses, so the schema sees one
 * international number. A "+..." typed into the box wins over the picker.
 */
export function joinPhone(country, national) {
  const typed = (national ?? '').trim();
  if (!typed) return '';
  if (typed.startsWith('+')) return typed;
  if (!PHONE_CHARACTERS.test(typed)) return `+${getCountryCallingCode(country)} ${typed}`;
  const number = parsePhoneNumberFromString(typed, country);
  return number ? number.number : `+${getCountryCallingCode(country)} ${typed}`;
}

/** The country a "+..." typed into the number box names, if any. */
export function countryOfInternational(typed) {
  if (!typed?.trim().startsWith('+')) return undefined;
  const number = parsePhoneNumberFromString(typed.trim());
  if (!number) return undefined;
  return number.country ?? mainCountryFor(number.countryCallingCode);
}

/** Tidy the national part once typed ("9876543210" -> "98765 43210"); left alone if invalid. */
export function formatNational(country, typed) {
  if (!typed || !PHONE_CHARACTERS.test(typed) || typed.trim().startsWith('+')) return typed;
  const number = parsePhoneNumberFromString(typed, country);
  return number?.isValid() ? withoutDialCode(number) : typed;
}
